/**
 * Export Utilities — CSV and JSON download helpers.
 * Frontend-only. No new API routes needed.
 * Uses a BOM prefix so Excel opens CSV files correctly on Windows.
 */

// ── Core download engine ──────────────────────────────────────────────────────

function triggerDownload(content: string, filename: string, mimeType: string): void {
  const blob = new Blob(["\uFEFF" + content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function toDateStr(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

// ── Generic CSV serializer ────────────────────────────────────────────────────

export function downloadCSV(rows: Record<string, unknown>[], filename: string): void {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);

  const escapeCell = (v: unknown): string => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escapeCell(r[h])).join(",")),
  ].join("\n");

  triggerDownload(csv, filename, "text/csv;charset=utf-8;");
}

export function downloadJSON(data: unknown, filename: string): void {
  triggerDownload(JSON.stringify(data, null, 2), filename, "application/json");
}

// ── Typed export helpers (one per data domain) ────────────────────────────────

export function exportSeatMembers(
  members: Array<{
    github_login: string;
    github_name: string | null;
    role: string;
    joined_at: string;
  }>,
  orgSlug: string
): void {
  const rows = members.map((m) => ({
    login: m.github_login,
    name: m.github_name ?? "",
    role: m.role,
    joined: m.joined_at.slice(0, 10),
  }));
  downloadCSV(rows, `paxley-seats-${orgSlug}-${toDateStr()}.csv`);
}

export function exportPlanHistory(
  history: Array<{
    from_plan: string;
    to_plan: string;
    changed_by: string;
    paystack_event: string | null;
    changed_at: string;
  }>,
  orgSlug: string
): void {
  const rows = history.map((h) => ({
    from_plan: h.from_plan,
    to_plan: h.to_plan,
    changed_by: h.changed_by,
    trigger: h.paystack_event ?? h.changed_by,
    date: h.changed_at.slice(0, 10),
    datetime: h.changed_at,
  }));
  downloadCSV(rows, `paxley-plan-history-${orgSlug}-${toDateStr()}.csv`);
}

export function exportUsageSnapshots(
  snapshots: Array<{
    snapshot_date: string;
    repos_count: number;
    members_count: number;
    scans_count: number;
  }>,
  orgSlug: string
): void {
  const rows = snapshots.map((s) => ({
    date: s.snapshot_date,
    repos: s.repos_count,
    members: s.members_count,
    scans: s.scans_count,
  }));
  downloadCSV(rows, `paxley-usage-analytics-${orgSlug}-${toDateStr()}.csv`);
}
